import { GRID_TYPES, PAPER, LETTER_PAPER, PAGE_PADDING, TITLE_HEIGHT, INFO_HEIGHT, FONT_OPTIONS } from './constants'
import { getPinyinArray } from './pinyin'
import { getStrokeOrderStrokes } from './stroke-loader'

import type { HanziConfig, PageUsage } from '../types'

const PINYIN_ABOVE_FONT_SCALE = 0.2
const PINYIN_ABOVE_BAND_RATIO = 1 / 3
const PINYIN_ABOVE_ROW_HEIGHT_UNITS = 1 + PINYIN_ABOVE_BAND_RATIO
const ROW_UNIT_PRECISION = 1000

export function getHanziSheetSize(paperSize: 'a4' | 'letter' = 'a4') {
  const paper = paperSize === 'letter' ? LETTER_PAPER : PAPER
  return { width: paper.width, height: paper.height }
}

export function getPageLayout(paperSize: 'a4' | 'letter' = 'a4') {
  const paper = paperSize === 'letter' ? LETTER_PAPER : PAPER
  const W = paper.width
  const H = paper.height
  const cellSize = paper.cellSize
  const P = PAGE_PADDING
  const titleY = P.top
  const infoY = titleY + TITLE_HEIGHT
  const gridStartY = infoY + INFO_HEIGHT
  const printableWidth = W - P.left - P.right
  const printableHeight = H - gridStartY - P.bottom
  const gridCols = Math.max(1, Math.floor(printableWidth / cellSize))
  const gridRows = Math.max(1, Math.floor(printableHeight / cellSize))
  const actualGridWidth = gridCols * cellSize
  const actualGridHeight = gridRows * cellSize
  const offsetX = P.left + (printableWidth - actualGridWidth) / 2
  const offsetY = gridStartY + (printableHeight - actualGridHeight) / 2

  return { W, H, P, cellSize, titleY, infoY, gridCols, gridRows, offsetX, offsetY }
}

export function drawA4Paper(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = '#E8E8E8'
  ctx.fillRect(0, 0, w, h)

  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.15)'
  ctx.shadowBlur = 6
  ctx.shadowOffsetX = 2
  ctx.shadowOffsetY = 2
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, w, h)
  ctx.restore()

  ctx.save()
  ctx.strokeStyle = '#D0D0D0'
  ctx.lineWidth = 0.3
  ctx.setLineDash([3, 3])
  ctx.strokeRect(1, 1, w - 2, h - 2)
  ctx.restore()
}

export function renderHanziSheet(ctx: CanvasRenderingContext2D, config: HanziConfig) {
  return renderHanziSheetPage(ctx, config, 0)
}

export function renderHanziSheetPage(
  ctx: CanvasRenderingContext2D,
  config: HanziConfig,
  pageIndex: number,
  fontFamily?: string
) {
  const {
    chars,
    gridType,
    traceCount,
    insertBlankRow,
    insertBlankCol,
    showStrokeOrder,
    traceColor,
    lineColor,
    fontFamily: configFontFamily,
    showPinyin,
    inputMode,
    words,
    paperSize,
  } = config

  const gridCfg = GRID_TYPES[gridType]
  const { W, H, cellSize, gridRows, gridCols, offsetX, offsetY } = getPageLayout(paperSize)
  const resolvedFontFamily = fontFamily || FONT_OPTIONS[configFontFamily]?.family || FONT_OPTIONS.wenkai.family

  if (!chars || chars.length === 0) return

  const units = buildPracticeUnits(chars, inputMode, words)
  if (!units.length) return

  ctx.clearRect(0, 0, W, H)
  drawA4Paper(ctx, W, H)

  const rowSpecs = buildRowSpecs(units, insertBlankRow, showStrokeOrder, traceCount, insertBlankCol, gridCols)
  const pagination = paginateRowSpecs(rowSpecs, gridRows, showPinyin)
  const pageRows = pagination.pages[pageIndex] || []

  pageRows.forEach(({ rowSpec, yUnits, heightUnits }) => {
    const y = offsetY + yUnits * cellSize
    const rowHeight = heightUnits * cellSize

    if (rowSpec.type === 'pinyin') {
      for (let col = 0; col < gridCols; col++) {
        const x = offsetX + col * cellSize
        drawPinyinGrid(ctx, x, y, cellSize, lineColor)
      }

      const rowCells = rowSpec.cells || buildRowCells(rowSpec.unit, traceCount, insertBlankCol, showStrokeOrder, gridCols)
      const maxRenderableCols = Math.min(gridCols, rowCells.length)
      for (let col = 0; col < maxRenderableCols; col++) {
        const cell = rowCells[col]
        if (!cell || cell.kind === 'blank') continue

        const x = offsetX + col * cellSize
        const cx = x + cellSize / 2
        const cy = y + rowHeight * 0.6

        if (cell.char) {
          const phoneticText = getPinyinArray(cell.char)[0] || ''
          if (phoneticText) {
            ctx.save()
            ctx.fillStyle = '#666666'
            ctx.font = `${cellSize * 0.28}px sans-serif`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(phoneticText, cx, cy)
            ctx.restore()
          }
        }
      }
      return
    }

    const hasPinyinAboveBand = (rowSpec.type === 'content' || rowSpec.type === 'empty') && showPinyin
    for (let col = 0; col < gridCols; col++) {
      const x = offsetX + col * cellSize
      drawGrid(ctx, x, y, cellSize, gridCfg, lineColor, hasPinyinAboveBand, rowHeight)
    }

    if (rowSpec.type !== 'content') return

    const rowCells = rowSpec.cells || buildRowCells(rowSpec.unit, traceCount, insertBlankCol, showStrokeOrder, gridCols)
    const maxRenderableCols = Math.min(gridCols, rowCells.length)

    for (let col = 0; col < maxRenderableCols; col++) {
      const cell = rowCells[col]
      if (!cell || cell.kind === 'blank') continue
      if (!cell.char) continue

      const x = offsetX + col * cellSize
      const cx = x + cellSize / 2
      const contentTop = hasPinyinAboveBand ? y + cellSize * PINYIN_ABOVE_BAND_RATIO : y
      const contentBottom = hasPinyinAboveBand ? contentTop + cellSize : y + rowHeight
      const cy = contentTop + (contentBottom - contentTop) / 2 + 1
      const isSample = cell.kind === 'sample'
      const charCenterY = cy
      const fontSize = cellSize * 0.64
      const strokes = getStrokeOrderStrokes(cell.char || '')

      if (cell.kind === 'stroke') {
        if (strokes.length) {
          drawStrokeOrderChar(ctx, strokes, cell.strokeIndex || 1, cx, charCenterY, fontSize, traceColor, 0.35)
        }
        continue
      }

      if (showStrokeOrder && strokes.length && (cell.kind === 'sample' || cell.kind === 'trace')) {
        drawStrokeOrderChar(ctx, strokes, strokes.length, cx, charCenterY, fontSize, isSample ? '#333333' : traceColor, isSample ? 1 : 0.35)
        continue
      }

      drawTraceChar(ctx, cell.char, cx, charCenterY, fontSize, isSample ? '#333333' : traceColor, isSample ? 1 : 0.35, resolvedFontFamily)

      if (hasPinyinAboveBand) {
        const phoneticText = getPinyinArray(cell.char)[0] || ''
        if (phoneticText) {
          ctx.save()
          ctx.fillStyle = '#777777'
          ctx.font = `${cellSize * PINYIN_ABOVE_FONT_SCALE}px sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(phoneticText, cx, y + (cellSize * PINYIN_ABOVE_BAND_RATIO) / 2)
          ctx.restore()
        }
      }
    }
  })
}

export function getPageCount(config: HanziConfig): number {
  return getPageUsage(config).pageCount
}

export function getPageUsage(config: HanziConfig): PageUsage {
  const chars = (config.chars || '').trim()
  const { gridRows } = getPageLayout(config.paperSize)
  if (!chars) {
    return {
      pageCount: 0,
      usedRows: 0,
      totalRows: 0,
      remainingRows: 0,
      gridRows,
      isFullPage: false,
    }
  }

  const units = buildPracticeUnits(chars, config.inputMode, config.words)
  if (!units.length) {
    return {
      pageCount: 0,
      usedRows: 0,
      totalRows: 0,
      remainingRows: 0,
      gridRows,
      isFullPage: false,
    }
  }

  const { gridCols } = getPageLayout(config.paperSize)
  const rowSpecs = buildRowSpecs(units, config.insertBlankRow, config.showStrokeOrder, config.traceCount, config.insertBlankCol, gridCols)
  const pagination = paginateRowSpecs(rowSpecs, gridRows, config.showPinyin)
  const usedRows = roundRowUnits(pagination.usedRowUnits)
  const pageCount = Math.max(1, pagination.pageCount)
  const totalRows = roundRowUnits(pageCount * gridRows)
  const remainingRows = roundRowUnits(Math.max(0, totalRows - usedRows))

  return {
    pageCount,
    usedRows,
    totalRows,
    remainingRows,
    gridRows,
    isFullPage: remainingRows === 0,
  }
}

function buildPracticeUnits(text: string, inputMode: 'char' | 'word', words?: string[]): string[][] {
  const normalized = (text || '').trim()
  if (!normalized) return []

  if (inputMode === 'word' && words && words.length > 0) {
    return words
      .map((word) =>
        word
          .replace(/[^\u4e00-\u9fa5]/g, '')
          .split('')
          .filter(Boolean)
      )
      .filter((unit) => unit.length > 0)
  }

  return normalized
    .replace(/[^\u4e00-\u9fa5]/g, '')
    .split('')
    .filter(Boolean)
    .map((ch) => [ch])
}

type RowCell = {
  kind: 'sample' | 'trace' | 'stroke' | 'blank'
  char?: string
  strokeIndex?: number
}

type RowSpec = {
  type: 'pinyin' | 'content' | 'blank-gap' | 'empty'
  unit: string[]
  cells?: RowCell[]
}

type PositionedRowSpec = {
  rowSpec: RowSpec
  yUnits: number
  heightUnits: number
}

function buildRowSpecs(
  units: string[][],
  insertBlankRow: boolean,
  showStrokeOrder: boolean,
  traceCount: number,
  insertBlankCol: boolean,
  gridCols: number
): RowSpec[] {
  const rows: RowSpec[] = []

  units.forEach((unit) => {
    const contentRows = buildContentRowSpecs(unit, showStrokeOrder, traceCount, insertBlankCol, gridCols)

    contentRows.forEach((contentRow) => {
      rows.push(contentRow)
    })

    if (insertBlankRow) {
      rows.push({ type: 'blank-gap', unit: [] })
    }
  })

  return rows
}

function buildContentRowSpecs(
  unit: string[],
  showStrokeOrder: boolean,
  traceCount: number,
  insertBlankCol: boolean,
  gridCols: number
): RowSpec[] {
  if (!showStrokeOrder) return [{ type: 'content', unit }]

  const rowCells = buildRowCells(unit, traceCount, insertBlankCol, true, gridCols)
  const rows: RowSpec[] = []

  for (let i = 0; i < rowCells.length; i += gridCols) {
    rows.push({ type: 'content', unit, cells: rowCells.slice(i, i + gridCols) })
  }

  return rows
}

function paginateRowSpecs(
  rowSpecs: RowSpec[],
  gridRows: number,
  showPinyin: boolean
): { pages: PositionedRowSpec[][], pageCount: number, usedRowUnits: number } {
  const pages: PositionedRowSpec[][] = [[]]
  let pageIndex = 0
  let usedOnPage = 0
  let usedRowUnits = 0

  rowSpecs.forEach((rowSpec) => {
    const heightUnits = getRowHeightUnits(rowSpec, showPinyin)
    if (pages[pageIndex].length > 0 && usedOnPage + heightUnits > gridRows) {
      pageIndex += 1
      pages[pageIndex] = []
      usedOnPage = 0
    }

    pages[pageIndex].push({
      rowSpec,
      yUnits: roundRowUnits(usedOnPage),
      heightUnits,
    })

    usedOnPage = roundRowUnits(usedOnPage + heightUnits)
    usedRowUnits = roundRowUnits(usedRowUnits + heightUnits)
  })

  const emptyRowHeightUnits = getRowHeightUnits({ type: 'empty', unit: [] }, showPinyin)
  pages.forEach((pageRows) => {
    const filledOnPage = pageRows.length > 0
      ? roundRowUnits(pageRows[pageRows.length - 1].yUnits + pageRows[pageRows.length - 1].heightUnits)
      : 0

    let emptyYUnits = filledOnPage
    while (roundRowUnits(emptyYUnits + emptyRowHeightUnits) <= gridRows) {
      pageRows.push({
        rowSpec: { type: 'empty', unit: [] },
        yUnits: emptyYUnits,
        heightUnits: emptyRowHeightUnits,
      })
      emptyYUnits = roundRowUnits(emptyYUnits + emptyRowHeightUnits)
    }
  })

  const pageCount = rowSpecs.length > 0 ? pages.length : 0
  return { pages, pageCount, usedRowUnits }
}

function getRowHeightUnits(
  rowSpec: RowSpec,
  showPinyin: boolean
): number {
  if ((rowSpec.type === 'content' || rowSpec.type === 'empty') && showPinyin) {
    return PINYIN_ABOVE_ROW_HEIGHT_UNITS
  }

  return 1
}

function roundRowUnits(value: number): number {
  return Math.round(value * ROW_UNIT_PRECISION) / ROW_UNIT_PRECISION
}

function buildRowCells(
  unitChars: string[],
  traceCount: number,
  insertBlankCol: boolean,
  showStrokeOrder: boolean,
  gridCols: number
): RowCell[] {
  const cells: RowCell[] = []
  appendUnit(cells, unitChars, 'sample', insertBlankCol)

  if (showStrokeOrder) {
    const ch = unitChars[0]
    const strokes = getStrokeOrderStrokes(ch)
    const firstRowStrokeSlots = Math.max(0, gridCols - cells.length)
    const remainingStrokes = Math.max(0, strokes.length - firstRowStrokeSlots)
    const extraRows = Math.ceil(remainingStrokes / gridCols)
    const fillCount = firstRowStrokeSlots + extraRows * gridCols

    for (let i = 0; i < Math.max(1, fillCount); i++) {
      if (strokes[i]) {
        cells.push({ kind: 'stroke', char: ch, strokeIndex: i + 1 })
      } else {
        cells.push({ kind: 'trace', char: ch })
      }
    }

    return cells
  }

  for (let i = 0; i < traceCount; i++) {
    if (insertBlankCol) cells.push({ kind: 'blank' })
    appendUnit(cells, unitChars, 'trace', insertBlankCol)
  }

  return cells
}

function appendUnit(
  cells: RowCell[],
  unitChars: string[],
  kind: 'sample' | 'trace',
  insertBlankCol: boolean
) {
  unitChars.forEach((ch, idx) => {
    cells.push({ kind, char: ch })
    if (insertBlankCol && idx < unitChars.length - 1) cells.push({ kind: 'blank' })
  })
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  gridCfg: { lines: readonly string[] },
  lineColor: string,
  usePinyinAboveLayout: boolean = false,
  totalHeight: number = size
) {
  const left = x
  const top = y
  const right = x + size
  const bottom = y + totalHeight
  const cx = x + size / 2
  const contentTop = usePinyinAboveLayout ? y + size * PINYIN_ABOVE_BAND_RATIO : y
  const contentHeight = usePinyinAboveLayout ? size : bottom - contentTop
  const horizontalY = contentTop + contentHeight / 2

  ctx.strokeStyle = lineColor
  ctx.lineWidth = 1
  ctx.strokeRect(left, top, size, totalHeight)

  if (usePinyinAboveLayout) {
    ctx.beginPath()
    ctx.moveTo(left, contentTop)
    ctx.lineTo(right, contentTop)
    ctx.stroke()
  }

  if (gridCfg.lines.includes('cross')) {
    ctx.beginPath()
    ctx.moveTo(left, horizontalY)
    ctx.lineTo(right, horizontalY)
    ctx.moveTo(cx, contentTop)
    ctx.lineTo(cx, bottom)
    ctx.stroke()
  }

  if (gridCfg.lines.includes('diagonal')) {
    ctx.save()
    ctx.globalAlpha = 0.4
    ctx.setLineDash([3, 3])
    ctx.beginPath()
    ctx.moveTo(left, contentTop)
    ctx.lineTo(right, bottom)
    ctx.moveTo(right, contentTop)
    ctx.lineTo(left, bottom)
    ctx.stroke()
    ctx.restore()
  }
}

function drawPinyinGrid(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  lineColor: string
) {
  ctx.save()
  ctx.strokeStyle = lineColor
  ctx.globalAlpha = 0.3
  ctx.setLineDash([2, 2])
  ctx.lineWidth = 0.8
  ctx.strokeRect(x, y, size, size)
  ctx.restore()
}

function drawTraceChar(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  fontSize: number,
  color: string,
  opacity: number,
  fontFamily?: string
) {
  ctx.save()
  ctx.globalAlpha = opacity
  ctx.fillStyle = color
  ctx.font = `${fontSize}px ${fontFamily || '"LXGW WenKai", "STKaiti", "KaiTi", "Kaiti SC", "楷体", serif'}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, cx, cy)
  ctx.restore()
}

function drawStrokeOrderChar(
  ctx: CanvasRenderingContext2D,
  strokes: string[],
  visibleStrokeCount: number,
  cx: number,
  cy: number,
  fontSize: number,
  color: string,
  opacity: number
) {
  if (!strokes.length || visibleStrokeCount <= 0) return

  const bounds = getPathBounds(strokes)
  if (!bounds) return

  const scale = fontSize / 900
  const drawWidth = bounds.width * scale
  const drawHeight = bounds.height * scale
  const offsetX = cx - drawWidth / 2 - bounds.minX * scale
  const offsetY = cy + drawHeight / 2 + bounds.minY * scale

  ctx.save()
  ctx.globalAlpha = opacity
  ctx.fillStyle = color
  ctx.translate(offsetX, offsetY)
  ctx.scale(scale, -scale)

  const max = Math.min(visibleStrokeCount, strokes.length)
  for (let i = 0; i < max; i++) {
    drawSvgPath(ctx, strokes[i])
  }

  ctx.restore()
}

function getPathBounds(paths: string[]) {
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  paths.forEach((path) => {
    const tokens = path.match(/-?\d+(?:\.\d+)?/g)
    if (!tokens) return

    for (let i = 0; i < tokens.length - 1; i += 2) {
      const x = Number(tokens[i])
      const y = Number(tokens[i + 1])
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }
  })

  if (!Number.isFinite(minX)) return null

  return {
    minX, minY, maxX, maxY,
    width: maxX - minX,
    height: maxY - minY
  }
}

function drawSvgPath(ctx: CanvasRenderingContext2D, pathStr: string) {
  const re = /([MLCQZ])\s*([^MLCQZ]*)/g
  let match: RegExpExecArray | null

  ctx.beginPath()

  while ((match = re.exec(pathStr)) !== null) {
    const cmd = match[1]
    const nums = match[2].trim().split(/\s+/).filter((s) => s !== '').map(Number)

    switch (cmd) {
      case 'M': {
        const x = nums[0]; const y = nums[1]
        ctx.moveTo(x, y)
        break
      }
      case 'L': {
        const x = nums[0]; const y = nums[1]
        ctx.lineTo(x, y)
        break
      }
      case 'C': {
        const cp1x = nums[0]; const cp1y = nums[1]
        const cp2x = nums[2]; const cp2y = nums[3]
        const x = nums[4]; const y = nums[5]
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y)
        break
      }
      case 'Q': {
        const cpx = nums[0]; const cpy = nums[1]
        const x = nums[2]; const y = nums[3]
        ctx.quadraticCurveTo(cpx, cpy, x, y)
        break
      }
      case 'Z': {
        ctx.closePath()
        break
      }
    }
  }

  ctx.fill()
}
